import React, { useState } from 'react';
import PropTypes from 'prop-types';
import alertImg from 'assets/images/alert.png';
import { searchQueryMinLength, status } from 'constants/general';
import Loader from 'modules/common/components/Loader';
import tableStrings from './strings';
import {
  TableWrapper,
  TableGridWrapper,
  TableHeader,
  TableDivider,
  TableItem,
  TableItemText,
  TableItemStatus,
  EmptyState,
  EmptyStateText,
  EmptyStateSubText,
  Img,
  Center,
  TableItemLink,
  ShowMoreButton,
  TableItemWithPhoto,
  TableItemPhoto,
} from './Table.style';
import EditButtons from './EditButtons';
import { TableItemType } from './enums';

const TableRow = ({ item }) => {
  const { itemContent, itemType, itemPath } = item;
  switch (itemType) {
    case TableItemType.Link:
      return <TableItemLink to={itemPath}>{itemContent}</TableItemLink>;
    case TableItemType.Text:
      return <TableItemText>{itemContent}</TableItemText>;
    case TableItemType.TextWithPhoto:
      return (
        <TableItemWithPhoto>
          <TableItemPhoto src={item?.itemContentURL} />
          <TableItemText>{itemContent}</TableItemText>
        </TableItemWithPhoto>
      );
    case TableItemType.Status:
      return (
        <TableItemStatus isActive={itemContent}>
          {itemContent ? 'Active' : 'Deactivated'}
        </TableItemStatus>
      );
    case TableItemType.Component:
      return itemContent;
    default:
      return null;
  }
};

const Table = props => {
  const {
    latestDataLength,
    dataStatus,
    columns,
    rows,
    searchQuery,
    handleShowMoreClick,
    extendTableStyle,
    showTableDivider = true,
  } = props;
  const [isShowMore, setIsShowMore] = useState(false);
  const { emptyStateText, emptyStateSubText } = tableStrings;

  if (dataStatus === status.FETCHING && !isShowMore) {
    return (
      <Center>
        <Loader width={60} height={60} thickness={8} />
      </Center>
    );
  }

  return (
    <>
      {rows?.length === 0 &&
        (searchQuery.length >= searchQueryMinLength ||
          (props?.filtersSelected && Object.keys(props?.filtersSelected).length > 0)) && (
          <EmptyState>
            <Img src={alertImg} />
            <EmptyStateText>{emptyStateText}</EmptyStateText>
            <EmptyStateSubText>{emptyStateSubText}</EmptyStateSubText>
          </EmptyState>
        )}
      {rows?.length > 0 && (
        <TableWrapper>
          <TableGridWrapper
            extendTableStyle={extendTableStyle}
            columns={Object.keys(columns).length - 1}
          >
            {Object.keys(columns).map(GridHeaderKey => (
              <TableHeader key={GridHeaderKey}>{columns[GridHeaderKey]}</TableHeader>
            ))}
            <TableHeader key="edit" noBorder>
              Edit
            </TableHeader>
            {showTableDivider && <TableDivider />}
            {rows?.map(({ id, rowContent, editButtons }, index) => (
              <React.Fragment key={id}>
                {rowContent.map(item => (
                  <TableItem
                    onClick={() => {
                      if (item.isItemClickable) item.itemClickAction();
                    }}
                    key={item.itemContent}
                    isItemClickable={item.isItemClickable}
                  >
                    <TableRow item={item} />
                  </TableItem>
                ))}
                <TableItem noBorder>
                  <EditButtons editButtons={editButtons} />
                </TableItem>
                {showTableDivider && !(rows.length === index + 1) && <TableDivider />}
              </React.Fragment>
            ))}
          </TableGridWrapper>
        </TableWrapper>
      )}
      {latestDataLength === 10 && (
        <ShowMoreButton
          onClick={() => {
            setIsShowMore(true);
            handleShowMoreClick();
          }}
        >
          {dataStatus === status.FETCHING && isShowMore ? (
            <Loader width={25} height={25} thickness={4} />
          ) : (
            <span>{tableStrings.showMore}</span>
          )}
        </ShowMoreButton>
      )}
    </>
  );
};

Table.propTypes = {
  dataStatus: PropTypes.string,
  columns: PropTypes.shape({}),
  rows: PropTypes.shape({
    id: PropTypes.number,
    rowContent: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  searchQuery: PropTypes.string,
  handleShowMoreClick: PropTypes.func.isRequired,
  extendTableStyle: PropTypes.shape({}),
  showTableDivider: PropTypes.bool,
};

Table.defaultProps = {
  searchQuery: '',
};

export default Table;
